import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  GroupByPayDatePunchEntry,
  PayDateGroupEntry,
  TimeCardCacheKeys,
  useAccountGet,
  useApi,
  useEmployeePermissionsGet,
} from '../index';

export const WEB_PUNCH_LINK = '/web-punch';

export enum ProfileRole {
  Desktop = 'Desktop',
  Mobile = 'Mobile',
}

export const useWebPunchSession = () => {
  const { data, isLoading } = useAccountGet();

  return {
    isLoading,
    isMobileSession: data?.tokenProfileRole === ProfileRole.Mobile,
  };
};

export const useWebPunchAllowed = () => {
  const { data: account, isLoading: isLoadingAccount } = useAccountGet();
  const { data: permissions, isLoading: isLoadingPermissions } =
    useEmployeePermissionsGet();

  return {
    isLoading: isLoadingAccount || isLoadingPermissions,
    isWebPunchAllowed:
      !!permissions?.enableAppPunching &&
      !!permissions?.currentEmployeeAllowAppPunching &&
      !!account?.tokenProfileWebPunchAllowed,
  };
};

export const IN_COLUMN = 1;
export const OUT_COLUMN = 2;

const organizePunches = (
  data?: Record<string, PayDateGroupEntry[]>
): GroupByPayDatePunchEntry[][] => {
  const rows: GroupByPayDatePunchEntry[][] = [];

  const hasNoPunches =
    !data ||
    Object.values(data).every((groups) =>
      groups.every((group) => !group.punches?.length)
    );

  if (!data || hasNoPunches) {
    return [[]];
  }

  for (const groups of Object.values(data)) {
    for (const group of groups) {
      const punchesByRow: Record<string, GroupByPayDatePunchEntry[]> = {};

      for (const punch of group.punches ?? []) {
        const rowNumber = String(punch.timeCardRowNumber);
        if (!punchesByRow[rowNumber]) {
          punchesByRow[rowNumber] = [];
        }
        punchesByRow[rowNumber].push(punch);
      }

      for (const rowNumber in punchesByRow) {
        const punchesForThisRow = punchesByRow[rowNumber];
        punchesForThisRow.sort(
          (a, b) =>
            (a.timeCardColumnNumber ?? 0) - (b.timeCardColumnNumber ?? 0)
        );
        rows.push(punchesForThisRow);
      }
    }
  }

  return rows;
};

export const getPunchState = (
  organizedPunches: GroupByPayDatePunchEntry[][]
) => {
  const lastPunch = organizedPunches[organizedPunches.length - 1] ?? [];

  const hasInPunch = lastPunch.some(
    (punch) => punch.timeCardColumnNumber === IN_COLUMN
  );
  const hasOutPunch = lastPunch.some(
    (punch) => punch.timeCardColumnNumber === OUT_COLUMN
  );

  return { isPunchedIn: hasInPunch && !hasOutPunch };
};

export const useTodayPunches = (facilityEmployeeId?: number) => {
  const api = useApi();
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);

  const response = useQuery({
    queryKey: [TimeCardCacheKeys.WEB_PUNCH_TODAY, facilityEmployeeId, today],
    queryFn: () =>
      api.timeCardApi
        .apiV2TimecardEmployeeIdGroupByPayDateGet({
          employeeId: facilityEmployeeId as number,
          startDate: today.toISOString(),
          endDate: today.toISOString(),
        })
        .then((r) => r.data),
    enabled: Boolean(facilityEmployeeId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const organizedPunches = useMemo(
    () => organizePunches(response.data),
    [response.data]
  );

  return {
    ...response,
    organizedPunches,
    ...getPunchState(organizedPunches),
  };
};

export const useWebPunch = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(
    () => api.mobileV2Api.apiV2MobilePunchPost().then((r) => r.data),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([TimeCardCacheKeys.WEB_PUNCH_TODAY])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};
